import type { PaginatedListMeta } from '@/types/PaginatedListMeta';
import type { PaginatedListResponse } from '@/types/PaginatedListResponse';
import type { Product } from '@/types/Product';

export const STORE_PRODUCTS_PER_PAGE = 24;

export type StoreProductSort =
  | ''
  | 'price_asc'
  | 'price_desc'
  | 'newest'
  | 'oldest'
  | 'name_asc'
  | 'name_desc';
export type StoreStockFilter = 'in_stock' | 'out_of_stock';

export type StoreProductFilters = {
  categoryId?: number | null;
  subcategoryId?: number | null;
  priceRange?: [number, number] | null;
  stock?: StoreStockFilter | null;
};

export type StoreFilterCategory = {
  id: number;
  label: string;
  visibility: boolean;
  image_url?: string | null;
  product_count: number;
  subcategories?: StoreFilterCategory[];
};

export type StoreFiltersResponse = {
  data: {
    categories: StoreFilterCategory[];
    price_range: {
      min: number;
      max: number;
    };
    stock: {
      in_stock: number;
      out_of_stock: number;
    };
    total: number;
  };
};

export type StoreFilterQueryOptions = {
  merchantId: number | string;
  search?: string;
  filters?: StoreProductFilters;
};

export type StoreProductQueryOptions = StoreFilterQueryOptions & {
  page?: number;
  sort?: StoreProductSort;
};

export const STORE_PRODUCT_SORT_OPTIONS: Array<{
  title: string;
  value: StoreProductSort;
}> = [
  { title: 'Sort by', value: '' },
  { title: 'Price: Low to high', value: 'price_asc' },
  { title: 'Price: High to low', value: 'price_desc' },
  { title: 'Newest', value: 'newest' },
  { title: 'Oldest', value: 'oldest' },
  { title: 'Name: A to Z', value: 'name_asc' },
  { title: 'Name: Z to A', value: 'name_desc' },
];

export const getStoreProductSortValue = (sort: StoreProductSort) => {
  const sortMap: Record<StoreProductSort, string> = {
    '': '',
    price_asc: 'price',
    price_desc: '-price',
    newest: '-created_at',
    oldest: 'created_at',
    name_asc: 'title',
    name_desc: '-title',
  };

  return sortMap[sort];
};

const buildStoreFilterParams = ({
  search,
  filters,
}: Omit<StoreFilterQueryOptions, 'merchantId'>) => {
  const normalizedSearch = search?.trim();

  return [
    ...(normalizedSearch
      ? [`filter[search]=${encodeURIComponent(normalizedSearch)}`]
      : []),
    ...(filters?.categoryId ? [`filter[category]=${filters.categoryId}`] : []),
    ...(filters?.subcategoryId
      ? [`filter[subcategory]=${filters.subcategoryId}`]
      : []),
    ...(filters?.priceRange
      ? [
          `filter[price_range]=${filters.priceRange[0]},${filters.priceRange[1]}`,
        ]
      : []),
    ...(filters?.stock ? [`filter[stock]=${filters.stock}`] : []),
  ];
};

export const buildStoreFiltersQuery = ({
  merchantId,
  search,
  filters,
}: StoreFilterQueryOptions) => {
  const params = buildStoreFilterParams({ search, filters });
  return `merchants/${merchantId}/filters${params.length ? `?${params.join('&')}` : ''}`;
};

export const buildStoreProductQuery = ({
  merchantId,
  page = 1,
  search,
  sort = '',
  filters,
}: StoreProductQueryOptions) => {
  const sortValue = getStoreProductSortValue(sort);
  const params = [
    `page=${page}`,
    `per_page=${STORE_PRODUCTS_PER_PAGE}`,
    'include=photos,seller.accountable.logo,seller.accountable.coverPhoto',
    ...buildStoreFilterParams({ search, filters }),
    ...(sortValue ? [`sort=${sortValue}`] : []),
  ];

  return `merchants/${merchantId}/products?${params.join('&')}`;
};

export default function useStoreProducts() {
  const { data, pending, error, execute } =
    useGetData<PaginatedListResponse<Product>>();
  const {
    data: filterData,
    pending: filtersPending,
    error: filtersError,
    execute: executeFilters,
  } = useGetData<StoreFiltersResponse>();
  const fallbackMeta = (total = 0): PaginatedListMeta => ({
    current_page: 1,
    last_page: Math.max(1, Math.ceil(total / STORE_PRODUCTS_PER_PAGE)),
    total,
  });

  const fetchProducts = async (options: StoreProductQueryOptions) => {
    await execute(buildStoreProductQuery(options));
  };

  const fetchFilters = async (options: StoreFilterQueryOptions) => {
    await executeFilters(buildStoreFiltersQuery(options));
  };

  const products = computed(() => data.value?.data ?? []);
  const meta = computed(
    () => data.value?.meta ?? fallbackMeta(products.value.length)
  );
  const filterCategories = computed(
    () => filterData.value?.data.categories ?? []
  );
  const priceRange = computed<[number, number]>(() => {
    const range = filterData.value?.data.price_range;
    return [range?.min ?? 0, range?.max ?? 0];
  });
  const stockCounts = computed(
    () => filterData.value?.data.stock ?? { in_stock: 0, out_of_stock: 0 }
  );
  const facetTotal = computed(() => filterData.value?.data.total ?? 0);

  return {
    data,
    pending,
    error,
    filterData,
    filtersPending,
    filtersError,
    products,
    meta,
    filterCategories,
    priceRange,
    stockCounts,
    facetTotal,
    fetchProducts,
    fetchFilters,
  };
}
